import React, { useEffect, useState } from "react";
import {
    PaymentElement,
    useStripe,
    useElements,
} from "@stripe/react-stripe-js";
import { toast } from "react-toastify";
import Button from "../Button/Button";
import { useSession } from "next-auth/react";
import { getEvents } from "@/helpers/google";
import moment from "moment-timezone";
import { useTranslations } from "next-intl";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import WithdrawalConsent, {
    recordWithdrawalConsent,
} from "@/components/Checkout/WithdrawalConsent";

export default function CheckoutFormMentor(props) {
    const stripe = useStripe();
    const elements = useElements();
    const t = useTranslations();

    const [message, setMessage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    // Exécution avant la fin du délai de rétractation : accord obligatoire
    // avant de payer (voir lib/withdrawalConsent.js).
    const [waiverChecked, setWaiverChecked] = useState(false);
    const [showWaiverError, setShowWaiverError] = useState(false);

    useEffect(() => {
        if (!stripe) {
            return;
        }

        const clientSecret = new URLSearchParams(window.location.search).get(
            "payment_intent_client_secret",
        );

        if (!clientSecret) {
            return;
        }

        stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
            switch (paymentIntent.status) {
                case "succeeded":
                    toast.success(t("CheckoutFormMentor_success"));
                    break;
                case "processing":
                    toast.info(t("CheckoutFormMentor_processing"));
                    break;
                case "requires_payment_method":
                    toast.error(
                        t("CheckoutFormMentor_requires_payment_method"),
                    );
                    break;
                default:
                    toast.error(t("CheckoutFormMentor_try"));
                    break;
            }
        });
    }, [stripe]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        if (!waiverChecked) {
            setShowWaiverError(true);
            return;
        }

        let stop = false;

        // Vérifie que le slot n'a pas été pris entre le moment où le mentor a cliqué sur le bouton et le moment où il a validé le paiement
        if (props.mentor.googleCalendarConnected) {
            const events = await getEvents(
                props.mentor.id,
                props.selectedSlot.start,
            );
            const busySlots = events.map((event) => {
                const start = moment
                    .utc(event.start.dateTime)
                    .tz(props.mentor.timezone);
                const end = moment
                    .utc(event.end.dateTime)
                    .tz(props.mentor.timezone);
                return { start, end };
            });
            if (busySlots) {
                // Si le créneau n'est plus disponible (il a été pris entre temps)
                let slot = props.selectedSlot;
                let isSlotFree = true;
                let timeBeforeReservation = 0;
                if (props.mentor.timeBeforeReservation) {
                    const timeBeforeReservationArray =
                        props.mentor.timeBeforeReservation.split(":");
                    timeBeforeReservation +=
                        parseInt(timeBeforeReservationArray[0]) * 60;
                    timeBeforeReservation +=
                        parseInt(timeBeforeReservationArray[1]) * 1;
                }

                let timeAfterReservation = 0;
                if (props.mentor.timeAfterReservation) {
                    const timeAfterReservationArray =
                        props.mentor.timeAfterReservation.split(":");
                    timeAfterReservation +=
                        parseInt(timeAfterReservationArray[0]) * 60;
                    timeAfterReservation +=
                        parseInt(timeAfterReservationArray[1]) * 1;
                }
                for (let j = 0; j < busySlots.length; j++) {
                    let tempStart = slot.start
                        .clone()
                        .subtract(timeBeforeReservation, "minutes");
                    let tempEnd = slot.end
                        .clone()
                        .add(timeAfterReservation, "minutes");
                    if (
                        tempStart.isBetween(
                            busySlots[j].start,
                            busySlots[j].end,
                            null,
                            "[)",
                        ) ||
                        tempEnd.isBetween(
                            busySlots[j].start,
                            busySlots[j].end,
                            null,
                            "(]",
                        ) ||
                        busySlots[j].start.isBetween(
                            tempStart,
                            tempEnd,
                            null,
                            "[)",
                        )
                    ) {
                        isSlotFree = false;
                    }
                }

                if (!isSlotFree) {
                    stop = true;
                    toast.error(t("CheckoutFormMentor_slot_taken"));
                    props.restartBooking();
                    return;
                }
            }
        }

        if (props.codeUsed && props.codeUsed.name) {
            // Check if code is valid
            const responseCode = await fetch("/api/call/get/code-access", {
                method: "post",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    code: props.codeUsed.name,
                    mentorPseudo: props.mentor.mentorPseudo,
                }),
            });

            const fetchedCode = await responseCode.json();

            if (!responseCode.ok) {
                toast.error(t("CheckoutFormMentor_invalidCode"));
                stop = true;
                props.restartFullBooking();
                return;
            }

            // Check if code is still valid
            if (!fetchedCode.isValid) {
                toast.error(t("CheckoutFormMentor_invalidCode"));
                stop = true;
                props.restartFullBooking();
                return;
            }
        }

        if (!stop) {
            setIsLoading(true);

            await recordWithdrawalConsent({ clientSecret: props.clientSecret });

            const { error } = await stripe.confirmPayment({
                elements,
                confirmParams: {
                    // Make sure to change this to your payment completion page
                    return_url: props.pro
                        ? `${process.env.NEXTAUTH_URL}/account?content=invoices&pro=1`
                        : `${process.env.NEXTAUTH_URL}/account?content=invoices`,
                },
                redirect: "if_required",
            });

            // This point will only be reached if there is an immediate error when
            // confirming the payment. Otherwise, your customer will be redirected to
            // your `return_url`. For some payment methods like iDEAL, your customer will
            // be redirected to an intermediate site first to authorize the payment, then
            // redirected to the `return_url`.
            if (error) {
                if (
                    error.type === "card_error" ||
                    error.type === "validation_error"
                ) {
                    toast.error(error.message);
                } else {
                    toast.error(t("CheckoutFormMentor_try"));
                }
            } else {
                props.setBookIsPaid(true);
                const event = new Event("visibilitychange");
                document.dispatchEvent(event);
            }

            setIsLoading(false);
        }
    };

    return (
        <form
            id="payment-form"
            onSubmit={handleSubmit}
            width="100%"
            className="mt-4"
        >
            <PaymentElement id="payment-element" />
            <WithdrawalConsent
                className="mt-5"
                consentKey="checkout_withdrawal_consent_call"
                checked={waiverChecked}
                onChange={(checked) => {
                    setWaiverChecked(checked);
                    if (checked) setShowWaiverError(false);
                }}
                showError={showWaiverError}
            />
            <div className="mt-5">
                <Button
                    type="primary"
                    block
                    progress
                    disabled={isLoading || !stripe || !elements}
                    id="submit"
                    isLoading={isLoading}
                >
                    <span id="button-text">
                        <div className="flex items-center justify-center gap-2">
                            <SolarIcon name="lock-password-linear" className="text-base" />
                            {t("Checkout_button_label")}
                        </div>
                    </span>
                </Button>
                <p className="mb-0 mt-2 text-center text-xs text-gray-400 dark:text-grayDark-500 flex items-center justify-center gap-1">
                    <SolarIcon name="shield-check-linear" className="text-sm" />
                    {t("Checkout_secure_payment")}
                </p>
            </div>
            {/* Show any error or success messages */}
            {message && <div id="payment-message">{message}</div>}
        </form>
    );
}
